import {Injectable, Input} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {SettingsService} from 'app/original-services/settings.service';
import {StorageService} from 'app/original-services/storage.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import get from 'lodash-es/get';
import {BankConnectedModel} from '../original-models/bank-connected.model';
import {BankConnectedProfileModel, BankConnectionType} from '../original-models/bank-connection-profile.model';
import {Moment} from 'moment';
import {BankAccountLimit} from '../pages/planning/common';
import {ActivityTrackerService} from './activity-tracker.service';
import {AreaKey} from 'app/original-models/level-type.model';
import {Bank} from 'app/original-models/bank.model';
import {Account} from 'app/original-models/account.model';
import {ConnectedAccounts} from 'app/original-models/connected-accounts.model';
import {SEPA_CURRENCY} from 'app/common/constants';

@Injectable({ providedIn: 'root' })
export class BankAccountsService {
    @Input() userNameChange: string;
    apiUrl: string;
    private _syncTransactionsSubject = new BehaviorSubject<any>(null);
    private _checkWebFormsSubject = new BehaviorSubject<any>(null);
    private _accountDeletedSubject = new BehaviorSubject<any>(null);
    accountDeleted$ = this._accountDeletedSubject.asObservable();
    private _connectedAccountsSubject = new BehaviorSubject<boolean>(null);
    connectedAccountsChanged$ = this._connectedAccountsSubject.asObservable();
    private _currencySubject = new BehaviorSubject<boolean>(null);
    currencyItem: any;
    private _headerSyncedSubject = new BehaviorSubject<any>(null);
    headerSyncedChanged$ = this._headerSyncedSubject.asObservable();
    private _fetchHeaderSyncSubject = new BehaviorSubject<boolean>(null);
    fetchHeaderSync$ = this._fetchHeaderSyncSubject.asObservable();
    constructor(
        private http: HttpClient,
        private settings: SettingsService,
        private storage: StorageService,
        private activityTrackerService: ActivityTrackerService
    ) {
        this.apiUrl = this.settings.apiUrl;
    }

    findBanks(
        provider: BankConnectionType,
        query: { search?: string; country?: string } = {},
    ): Observable<Bank[]> {
        let params = new HttpParams();
        if (query.search) {
            params = params.set('search', query.search);
        }
        if (query.country) {
            params = params.set('country', query.country);
        }
        const url = `${this.apiUrl}/companies/${this.storage.companyId}/providers/${provider.toUpperCase()}/banks/`;
        return this.http.get<object[]>(url, { params }).pipe(
            map((res) => res.map((row) => new Bank().setFromApiModel(row))),
        );
    }

    getBankConnections(): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http
            .get(`${this.apiUrl}/companies/${companyId}/bank-connections/`)
            .pipe(
                map((res: any) => {
                    return res.results.map((i) =>
                        new BankConnectedProfileModel().setFromApiModel(i)
                    );
                })
            );
    }

    changePin(bank: any): Observable<any> {
        let companyId = this.storage.companyId;
        if (companyId) {
            let url = `${this.apiUrl}/companies/${companyId}/bank-connections/${bank.id}/access-renewals/`;
            return this.http.post(url, {}).pipe(map((data: any) => {
                return data;
            }));
        }
    }

    saveOfflineAccount(req: any, log?): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http
            .post(`${this.apiUrl}/companies/${companyId}/accounts/`, req.model)
            .pipe(
                map((data: any) => {
                    return data;
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            if (val.id && log.target.foreignParams) {
                                log.target.foreignParams.push({key: 'accountId', value: val.id});
                            }
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    assignCompanyToAccount(req: any): Observable<any> {
        return this.http.post(`${this.apiUrl}/accounts/`, req).pipe(
            map((data: any) => {
                return data;
            })
        );
    }

    editOfflineAccount(req: any, log?): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http
            .patch(
                `${this.apiUrl}/companies/${companyId}/accounts/${req.accountId}/`,
                req.model
            )
            .pipe(
                map((data: any) => {
                    return data;
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    getCurrency() {
        if (this.currencyItem) {
            setTimeout(() => {
                this._currencySubject.next(this.currencyItem);
            }, 4);
        } else {
            this.currencyItem = this.storage.getStorageItem('currency');
            //  data.accounts && data.accounts[0] ? data.accounts[0].accountCurrency : '';
            this._currencySubject.next(this.currencyItem);
        }
        return this._currencySubject.asObservable();
    }

    getAccounts(bankId?: number, getActive?: boolean): Observable<Account[]> {
        let params = new HttpParams();
        if (bankId) {
            params.set('bank_id', bankId.toString());
        }
        let currency = this.storage.getStorageItem('currency');
        let companyId = this.storage.companyId;
        return this.http
            .get(`${this.apiUrl}/companies/${companyId}/bank-accounts/`, {
                params,
            })
            .pipe(
                map((data: any) => {
                    this.currencyItem = currency;
                    //  data.accounts && data.accounts[0] ? data.accounts[0].accountCurrency : '';
                    this._currencySubject.next(this.currencyItem);
                    let result = get(data, 'accounts').map((i) =>
                        new Account().setFromApiModel({...i, accountCurrency: currency})
                    );
                    return result;
                })
            );
    }

    getAccountWithBankConnectionId(
        bankConnectionId: number, getActive?: boolean
    ): Observable<Account[]> {
        const params = new HttpParams().set(
            'bank_connection_id',
            bankConnectionId.toString()
        );
        const companyId = this.storage.companyId;
        return this.http
            .get(`${this.apiUrl}/companies/${companyId}/bank-accounts/`, {
                params,
            })
            .pipe(
                map((data: Account[]) => {
                    // @ts-ignore
                    let result = get(data, 'accounts').map((i) =>
                        new Account().setFromApiModel(i)
                    );
                    return result;
                })
            );
    }

    updateAlias(data: any, accountId: number): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http.patch(
            `${this.apiUrl}/companies/${companyId}/bank-accounts/${accountId}/`,
            data
        )
        .pipe(
            map((data: any) => {
                return data;
            })
        );
    }

    pushLimits(companyId, accountId, limits: BankAccountLimit[]) {
        console.log('saving')
        console.log(limits)
        return this.http.post(
            `${this.apiUrl}/companies/${companyId}/accounts/${accountId}/limits/`,
            limits
        )
        .pipe(
            map((data: any) => {
                return data;
            })
        );
    }

    getLimits(companyId: number, accountId: number): Observable<BankAccountLimit[]> {
        return this.http.get(
            `${this.apiUrl}/companies/${companyId}/accounts/${accountId}/limits/`
        ).pipe(
            map((data: any) => {
                const results = Array.isArray(data) ? data : (data?.results || []);
                return results.map((i: any): BankAccountLimit => ({
                    id: i.id,
                    limit: i.limit,
                    start_date: i.start_date
                }));
            })
        );
    }

    updateHeaderWithAccounts(list) {
        return this._connectedAccountsSubject.next(list);
    }

    getConnectedAccounts(area?: any): Observable<ConnectedAccounts[]> {
        if (!area) {
            area = this.storage.getArea();
        }
        let params = '';
        if (area.key === AreaKey.Groups) {
            params = '?single=true';
        }
        let currency = this.storage.getStorageItem('currency');
        return this.http
            .get(`${this.apiUrl}/${area.key}/${area.id}/accounts/${params}`)
            .pipe(
                map((data: any) => {
                    if (area.key === AreaKey.Groups) {
                        if (data && data[`${area.id}`]) {
                            return data[`${area.id}`].accounts.map((i) =>
                                new ConnectedAccounts().setFromApiModel({...i, accountCurrency: currency})
                            );
                        } else {
                            return [];
                        }
                    }
                    return data.accounts.map((i) =>
                        new ConnectedAccounts().setFromApiModel({...i, accountCurrency: currency})
                    );
                })
            );
    }

    /**
     * Connected accounts usable as a SEPA sender: active (not DISCONNECTED/OFFLINE/API),
     * with an IBAN and in the SEPA currency. Shared by the wallet top-up dialogs and payables.
     */
    getConnectedSepaAccounts(area?: any): Observable<ConnectedAccounts[]> {
        return this.getConnectedAccounts(area).pipe(
            map((data: ConnectedAccounts[]) =>
                data
                    ? data.filter(
                          (item) =>
                              item.status !== 'DISCONNECTED' &&
                              item.status !== 'OFFLINE' &&
                              item.status !== 'API' &&
                              item.iban &&
                              item.accountCurrency === SEPA_CURRENCY
                      )
                    : []
            )
        );
    }

    getConnectedAccountsWithTotals(companyId?: number): Observable<any> {
        if (!companyId) {
            companyId = this.storage.companyId;
        }

        let currency = this.storage.getStorageItem('currency');
        return this.http
            .get(`${this.apiUrl}/companies/${companyId}/accounts/`)
            .pipe(
                map((data: any) => {
                    return {
                        accounts: data.accounts.map((i) =>
                            new ConnectedAccounts().setFromApiModel({...i, accountCurrency: i.currency})
                        ),
                        fiscalEnding: data?.fiscal_year_ending_balance ? data.fiscal_year_ending_balance : 0,
                        in3Months: data?.in_3_months_balance ? data.in_3_months_balance : 0,
                        nextMonthEnd: data?.next_month_balance ? data.next_month_balance : 0,
                        total: data?.total ? data.total : 0,
                        thisMonth: data?.current_month_balance ? data.current_month_balance : 0,
                        availableLimit: data?.available_limit ? data.available_limit : 0,
                        thisMonthLimit: data?.current_month_limit ? data.current_month_limit : 0,
                        fiscalEndingLimit: data?.fiscal_year_ending_limit ? data.fiscal_year_ending_limit : 0,
                        in3MonthsLimit: data.in_3_months_limit ? data.in_3_months_limit : 0,
                        nextMonthLimit: data?.next_month_limit ? data.next_month_limit : 0
                    };
                })
            );
    }

    getBankConnection() {
        let area = this.storage.getArea();
        let currency = this.storage.getStorageItem('currency');
        if (area.id) {
            return this.http.get(`${this.apiUrl}/${area.key}/${area.id}/banks/`).pipe(
                map((data: any) => {
                    return data.results.map((item) => new BankConnectedModel().setFromApiModel({
                        ...item,
                        accountCurrency: currency
                    }))
                })
            );
        } else {
            return new Observable();
        }
    }

    deleteConnectedAccounts(
        accountId: any,
        isTotalDeleting?: boolean,
        resetBalance?: boolean,
        log?
    ): Observable<ConnectedAccounts[]> {
        let companyId = this.storage.companyId;
        let params = '';
        if (!isTotalDeleting) {
            params = '?disconnect=1';
        }
        if (resetBalance) {
            params = !params ? '?' : params + '&';
            params += 'reset_balance=1';
        }
        return this.http
            .delete(
                `${this.apiUrl}/companies/${companyId}/accounts/${accountId}/${params}`
            )
            .pipe(
                map((res: any) => {
                    return res;
                }),
                tap({
                    next: val => {
                        if (log && log.info && log.target) {
                            this.activityTrackerService.log(log.info, log.target);
                        }
                    },
                    error: error => {
                      console.log('on error', error.message);
                    }
                })
            );
    }

    deleteProfileConnectedAccount(
        accountId: any,
        hasCompany?: boolean,
        isTotalDeleting?: boolean,
        log?
    ): Observable<ConnectedAccounts[]> {
        if (!hasCompany) {
            let companyId = this.storage.companyId;
            return this.http
                .delete(
                    `${this.apiUrl}/companies/${companyId}/bank-accounts/${accountId}/`
                )
                .pipe(
                    map((res: any) => {
                        return res;
                    }),
                    tap({
                        next: val => {
                            if (log && log.info && log.target) {
                                this.activityTrackerService.log(log.info, log.target);
                            }
                        },
                        error: error => {
                          console.log('on error', error.message);
                        }
                    })
                );
        } else {
            return this.deleteConnectedAccounts(accountId, isTotalDeleting, null, log);
        }
    }

    deleteProfileAllocatedCompany(
        accountId: any,
        companyId: any,
        isTotalDeleting?: boolean
    ): Observable<ConnectedAccounts[]> {
        let typeDelete = !isTotalDeleting ? '?disconnect=1' : '';
        return this.http
            .delete(
                `${this.apiUrl}/companies/${companyId}/accounts/${accountId}/${typeDelete}`
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    connectAccountsProfile(req: string): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http.delete(
            `${this.apiUrl}/companies/${companyId}/bank-accounts/?id=${req}`
        );
    }

    finalizeConnectAccounts(
        bankConnectionId: number,
        accounts: Account[]
    ): Observable<any> {
        const companyId = this.storage.companyId;
        return this.http.patch(
            `${this.apiUrl}/companies/${companyId}/bank-connections/${bankConnectionId}/`,
            {
                accounts: accounts.map((account) => account.id),
            }
        );
    }

    /**
     * Creates a bank connection (web form) for any provider. The API expects the provider code
     * upper-cased, which is this method's business, not the caller's.
     * `body` carries the provider-specific fields (`bank_id`, `ebics`, Konfipay request payload, ...).
     */
    createBankConnection(provider: BankConnectionType, body: object = {}, date?: Moment | null): Observable<any> {
        const companyId = this.storage.companyId;
        return this.http.post(`${this.apiUrl}/companies/${companyId}/bank-connections/`, {
            provider: provider.toUpperCase(),
            min_booking_date: date ? date.format('YYYY-MM-DD') : undefined,
            ...body
        });
    }

    checkWebForm(id: any): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http.get(
            `${this.apiUrl}/companies/${companyId}/web-forms/${id}/`
        );
    }

    putBankConnection(bankConnectionId: any): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http.post(
            `${this.apiUrl}/companies/${companyId}/bank-connections/${bankConnectionId}/account-selection/`,
            {}
        );
    }

    deleteBankConnection(bankConnectionId: any): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http.delete(
            `${this.apiUrl}/companies/${companyId}/bank-connections/${bankConnectionId}/`
        );
    }

    syncTransactions(companyId: number): Observable<any> {
        this._syncTransactionsSubject.next(null);
        return this.http.put(
            `${this.apiUrl}/companies/${companyId}/bank-connections/`,
            {}
        )
        .pipe(
            map((data: any) => {
                return data;
            })
        );
    }

    onSyncTransactions() {
        return this._syncTransactionsSubject.asObservable();
    }

    downloadEbics(bankConnectionId: any): Observable<any> {
        let companyId = this.storage.companyId;
        return this.http.get(
            `${this.apiUrl}/companies/${companyId}/bank-connections/${bankConnectionId}/challenge.pdf`, {
                responseType: 'blob',
            }
        );
    }

    checkWebFormsByIds(withRetry?: boolean): Observable<any> {
        let ids = this.storage.getStorageItem('webFormTaskIds');
        // this._checkSyncStatus(ids, withRetry);
        this._checkWebFormsSubject.next(null);
        return this._checkWebFormsSubject.asObservable();
    }

    headerStatusSynced(data) {
        return this._headerSyncedSubject.next(data);
    }

    fetchHeaderSync() {
        return this._fetchHeaderSyncSubject.next(true);
    }

    getGroupConnectedAccountsWithTotals(
        groupId: number,
        getSingle?: boolean
    ): Observable<any> {

        let currency = this.storage.getStorageItem('currency');
        if (getSingle) {
            const url = `${this.apiUrl}/corporate-groups/${groupId}/accounts/`;
            return this.http.get(url).pipe(map((data: any) => {
                return {
                    accounts: data.accounts.map((i) => new ConnectedAccounts().setFromApiModel({
                        ...i,
                        accountCurrency: i.currency
                    })),
                    fiscalEnding: data?.fiscal_year_ending_balance ? data.fiscal_year_ending_balance : 0,
                    in3Months: data?.in_3_months_balance ? data.in_3_months_balance : 0,
                    nextMonthEnd: data?.next_month_balance ? data.next_month_balance : 0,
                    total: data?.total ? data.total : 0,
                    thisMonth: data?.current_month_balance ? data.current_month_balance : 0,
                    availableLimit: data?.available_limit ? data.available_limit : 0,
                    thisMonthLimit: data?.current_month_limit ? data.current_month_limit : 0,
                    fiscalEndingLimit: data?.fiscal_year_ending_limit ? data.fiscal_year_ending_limit : 0,
                    in3MonthsLimit: data.in_3_months_limit ? data.in_3_months_limit : 0,
                    nextMonthLimit: data?.next_month_limit ? data.next_month_limit : 0
                };
            }));
        } else {
            const url = `${this.apiUrl}/corporate-groups/${groupId}/projected-balances/`;
            return this.http.get(url).pipe(map((data: any) => data));
        }
    }

    //extended parameter returns last transaction of account
    getAccountInfo(
        name: string,
        extended?: boolean
    ): Observable<Account[]> {
        const params = new HttpParams().set(
            'search',
            name
        );
        const companyId = this.storage.companyId;
        return this.http
            .get(`${this.apiUrl}/companies/${companyId}/accounts/${extended ? 'extended/': ''}`, {
                params,
            })
            .pipe(
                map((data: Account[]) => {
                    // @ts-ignore
                    return get(data, 'accounts').map((i) =>
                        new Account().setFromApiModel(i)
                    );
                })
            );
    }
}
